import { useEffect, useState } from 'react';
import { useGetAllProductResourcesQuery } from '../store/slices/apiSlice/product-api-slice';
import { objectDeepClone } from '../utilities/generalHelpers';

// Helpers
const EmptyProjectConfig = {
    project_id: null,
    siding_color_option_id: null,
    foundation_option_id: null,
    roof_option_id: null,
    deck_option_id: null,
    deck_riser_option_id: null,
};

const getProductOptions = (allProductResources) => {
    const { home_products } = allProductResources;
    const productOptions = [];
    for (const product of home_products) {
        productOptions.push({
            value: product.id,
            label: `${product.name} (${product.aro_id})`,
        });
    }
    return productOptions;
};

const getVersionOptionsForProduct = (productId, allProductResources) => {
    const { home_products } = allProductResources;
    const homeProduct = home_products.find((prod) => prod.id === productId);
    const versionOptions = homeProduct?.versions.map((version) => ({
        value: version.id,
        label: version.name,
    }));
    return versionOptions;
};

const getOptionTypesForProductVersion = (
    productId,
    productVersionId,
    allProductResources
) => {
    const { home_products } = allProductResources;
    const homeProduct = home_products.find((prod) => prod.id === productId);
    const productVersions = homeProduct.versions;
    const version = productVersions.find(
        (version) => version.id === productVersionId
    );
    return version?.option_types || [];
};

// Component
function useManageProjectInfo() {
    const { data: allProductResources, isLoading } =
    useGetAllProductResourcesQuery();
    const [projectInfo, setProjectInfo] = useState({});
    const [projectResourceOptions, setProjectResourceOptions] = useState(null);

    const initializeProjectInfo = (infoFromQueryProject) => {
        setProjectInfo(infoFromQueryProject);
    };

    const updateProjectInfo = (updates) => {
        const updatedProjectInfo = objectDeepClone(projectInfo);
        updates.forEach(({ sectionName, fieldName, value }) => {
            const updatedValue = value == 0 ? null : value;
            updatedProjectInfo[sectionName][fieldName] = updatedValue;

            switch (fieldName) {
            case 'home_product_id':
                updatedProjectInfo.Project.product_version_id = null;
                updatedProjectInfo.Project.nta_version_id = null;
                updatedProjectInfo.ProjectProductConfiguration = {
                    ...EmptyProjectConfig,
                    project_id: projectInfo.Project.id,
                };
                setProjectResourceOptions((options) => ({
                    home_products: options?.home_products,
                    product_versions: getVersionOptionsForProduct(
                        projectInfo?.Project?.home_product_id,
                        allProductResources
                    ),
                }));
                break;
            case 'product_version_id':
                updatedProjectInfo.ProjectProductConfiguration = {
                    ...EmptyProjectConfig,
                    project_id: projectInfo.Project.id,
                };
                setProjectResourceOptions((options) => ({
                    ...options,
                    option_types: getOptionTypesForProductVersion(
                        projectInfo?.Project?.home_product_id,
                        projectInfo.Project?.product_version_id,
                        allProductResources
                    ),
                }));
                break;
            default:
                break;
            }
        });
        setProjectInfo(updatedProjectInfo);
    };

    useEffect(() => {
        if (projectInfo?.Project?.home_product_id && !isLoading) {
            setProjectResourceOptions((options) => ({
                home_products: options?.home_products,
                product_versions: getVersionOptionsForProduct(
                    projectInfo?.Project?.home_product_id,
                    allProductResources
                ),
            }));
        }
    }, [projectInfo?.Project?.home_product_id, isLoading]);

    useEffect(() => {
        if (projectInfo?.Project?.product_version_id && !isLoading) {
            setProjectResourceOptions((options) => ({
                ...options,
                option_types: getOptionTypesForProductVersion(
                    projectInfo?.Project?.home_product_id,
                    projectInfo.Project?.product_version_id,
                    allProductResources
                ),
            }));
        }
    }, [projectInfo?.Project?.product_version_id]);

    useEffect(() => {
        if (allProductResources) {
            setProjectResourceOptions({
                home_products: getProductOptions(allProductResources),
            });
        }
    }, [allProductResources]);

    useEffect(() => {
        if (projectInfo?.ProjectProductConfiguration && projectResourceOptions) {
            setProjectInfo(projectInfo);
        }
    }, [projectInfo]);

    return {
        initializeProjectInfo,
        projectInfo,
        projectResourceOptions,
        updateProjectInfo,
    };
}

export default useManageProjectInfo;
